import React, { useState, useEffect } from 'react';
import './dash.css';
import dashOne from '../Images/dashone.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { db } from '../firebase';
import { collection, query, getDocs, getCountFromServer, where, Timestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { normalizeAdminType, resolveAdminRecord } from '../utils/adminAccess';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function DashComp() {
  // States
  const [loggedInUser, setLoggedInUser] = useState('');
  const [adminRole, setAdminRole] = useState('admin');
  const [showFinancialData, setShowFinancialData] = useState(false);
  const [totalApplicants, setTotalApplicants] = useState(0);
  const [activeStudents, setActiveStudents] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyData, setMonthlyData] = useState({
    Jan: 0, Feb: 0, Mar: 0, Apr: 0, May: 0, Jun: 0,
    Jul: 0, Aug: 0, Sep: 0, Oct: 0, Nov: 0, Dec: 0
  });
  const [analytics, setAnalytics] = useState({
    funnel: {
      applications: 0,
      active: 0,
      graduated: 0,
      deferred: 0,
      dropped: 0,
      expelled: 0,
      didNotJoin: 0
    },
    intake: {
      last7: 0,
      prev7: 0,
      last30: 0,
      prev30: 0
    },
    topSources: [],
    topSourcesLatest: [],
    completionByCourse: [],
    dropReasons: [],
    cohortProgress: [],
    instructorLoad: [],
    debtRisk: {
      totalPrincipal: 0,
      dueSoon: 0,
      totalCount: 0
    }
  });

  // Generate list of years from 2020 to the current year
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2020; year <= currentYear; year++) {
    years.push(year);
  }

  // Function to handle button clicks
  const handleClick = (year) => {
    console.log(`Button clicked for year: ${year}`);
    setSelectedYear(year);
  };

  // Fetch data for the selected year
  useEffect(() => {
    const fetchData = async () => {
      const q = query(collection(db, 'Income'), where('paymentDate', '>=', Timestamp.fromDate(new Date(`${selectedYear}-01-01`))), where('paymentDate', '<=', Timestamp.fromDate(new Date(`${selectedYear}-12-31`))));
      const querySnapshot = await getDocs(q);
      const data = {
        Jan: 0, Feb: 0, Mar: 0, Apr: 0, May: 0, Jun: 0,
        Jul: 0, Aug: 0, Sep: 0, Oct: 0, Nov: 0, Dec: 0
      };

      querySnapshot.forEach((doc) => {
        const incomeAmount = doc.data().incomeAmount;
        const paymentDate = doc.data().paymentDate.toDate();
        const month = paymentDate.getMonth(); // 0 for January, 11 for December
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        data[monthNames[month]] += parseFloat(incomeAmount);
      });

      setMonthlyData(data);
    };

    fetchData();
  }, [selectedYear]);

  useEffect(() => {
    const fetchData = async () => {
      const q = query(collection(db, 'users'), where('userStatus', '==', 'inactive'));
      const countSnapshot = await getCountFromServer(q);
      setTotalApplicants(countSnapshot.data().count);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const toDate = (value) => {
      if (!value) return null;
      if (value.toDate) return value.toDate();
      if (value.seconds) return new Date(value.seconds * 1000);
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const fetchAnalytics = async () => {
      if (adminRole !== 'superAdmin') {
        return;
      }

      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const sourceCounts = {};
        const sourceCountsLatest = {};
        const courseStats = {};
        const cohortStats = {};
        const dropReasonCounts = {};
        const funnel = {
          applications: 0,
          active: 0,
          graduated: 0,
          deferred: 0,
          dropped: 0,
          expelled: 0,
          didNotJoin: 0
        };

        const now = new Date();
        const sevenAgo = new Date(now);
        sevenAgo.setDate(now.getDate() - 7);
        const fourteenAgo = new Date(now);
        fourteenAgo.setDate(now.getDate() - 14);
        const thirtyAgo = new Date(now);
        thirtyAgo.setDate(now.getDate() - 30);
        const sixtyAgo = new Date(now);
        sixtyAgo.setDate(now.getDate() - 60);
        let last7 = 0;
        let prev7 = 0;
        let last30 = 0;
        let prev30 = 0;

        usersSnap.forEach((doc) => {
          const data = doc.data();
          const status = String(data.userStatus || '').trim();
          const statusLower = status.toLowerCase();
          if (statusLower === 'inactive') funnel.applications += 1;
          if (statusLower === 'student') funnel.active += 1;
          if (statusLower === 'completed') funnel.graduated += 1;
          if (statusLower === 'deferred') funnel.deferred += 1;
          if (statusLower === 'dropped') funnel.dropped += 1;
          if (statusLower === 'expelled') funnel.expelled += 1;
          if (statusLower === 'did not join') funnel.didNotJoin += 1;

          const appDate = toDate(data.appDate);
          if (appDate) {
            if (appDate >= sevenAgo) last7 += 1;
            else if (appDate >= fourteenAgo) prev7 += 1;

            if (appDate >= thirtyAgo) last30 += 1;
            else if (appDate >= sixtyAgo) prev30 += 1;
          }

          const source = String(data.hear || data.source || 'Unknown').trim() || 'Unknown';
          sourceCounts[source] = (sourceCounts[source] || 0) + 1;
          if (appDate && appDate >= thirtyAgo) {
            sourceCountsLatest[source] = (sourceCountsLatest[source] || 0) + 1;
          }

          const course = String(data.course || 'Unknown').trim() || 'Unknown';
          if (!courseStats[course]) {
            courseStats[course] = { total: 0, completed: 0 };
          }
          courseStats[course].total += 1;
          if (statusLower === 'completed') {
            courseStats[course].completed += 1;
          }

          const cohort = String(data.cohort || 'Unknown').trim() || 'Unknown';
          if (!cohortStats[cohort]) {
            cohortStats[cohort] = { total: 0, completed: 0, active: 0 };
          }
          cohortStats[cohort].total += 1;
          if (statusLower === 'completed') cohortStats[cohort].completed += 1;
          if (statusLower === 'student') cohortStats[cohort].active += 1;

          if (statusLower === 'did not join') {
            const reason = String(data.notJoiningReason || 'No reason').trim() || 'No reason';
            dropReasonCounts[reason] = (dropReasonCounts[reason] || 0) + 1;
          }
        });

        const topSources = Object.entries(sourceCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name, count]) => ({ name, count }));

        const topSourcesLatest = Object.entries(sourceCountsLatest)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name, count]) => ({ name, count }));

        const completionByCourse = Object.entries(courseStats)
          .map(([name, stat]) => ({
            name,
            total: stat.total,
            completed: stat.completed,
            rate: stat.total ? Math.round((stat.completed / stat.total) * 100) : 0
          }))
          .sort((a, b) => b.rate - a.rate || b.total - a.total)
          .slice(0, 3);

        const dropReasons = Object.entries(dropReasonCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([reason, count]) => ({ reason, count }));

        const cohortProgress = Object.entries(cohortStats)
          .map(([name, stat]) => ({
            name,
            total: stat.total,
            completed: stat.completed,
            active: stat.active
          }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 3);

        const classesSnap = await getDocs(collection(db, 'allclasses'));
        const instructorCounts = {};
        classesSnap.forEach((doc) => {
          const data = doc.data();
          const instructor = data.instructor || 'Unknown';
          instructorCounts[instructor] = (instructorCounts[instructor] || 0) + 1;
        });

        const instructorSnap = await getDocs(collection(db, 'Employees'));
        const instructorMap = {};
        instructorSnap.forEach((doc) => {
          const data = doc.data();
          instructorMap[doc.id] = data.employeeName || data.name || 'Instructor';
        });

        const instructorLoad = Object.entries(instructorCounts)
          .map(([id, count]) => ({
            name: instructorMap[id] || id,
            count
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 3);

        const loansSnap = await getDocs(collection(db, 'Loans'));
        const dueSoonDate = new Date();
        dueSoonDate.setDate(dueSoonDate.getDate() + 30);
        let totalPrincipal = 0;
        let dueSoon = 0;
        loansSnap.forEach((doc) => {
          const data = doc.data();
          const principal = parseFloat(data.principalAmount || 0) || 0;
          totalPrincipal += principal;
          const dueDate = toDate(data.dueDate);
          if (dueDate && dueDate <= dueSoonDate) {
            dueSoon += 1;
          }
        });

        setAnalytics({
          funnel,
          intake: { last7, prev7, last30, prev30 },
          topSources,
          topSourcesLatest,
          completionByCourse,
          dropReasons,
          cohortProgress,
          instructorLoad,
          debtRisk: {
            totalPrincipal,
            dueSoon,
            totalCount: loansSnap.size
          }
        });
      } catch (error) {
        console.error('Failed to load dashboard analytics:', error);
      }
    };

    fetchAnalytics();
  }, [adminRole]);
  useEffect(() => {
    const fetchData = async () => {
      const q = query(collection(db, 'users'), where('userStatus', '==', 'Student'));
      const countSnapshot = await getCountFromServer(q);
      setActiveStudents(countSnapshot.data().count);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const q = query(
        collection(db, 'Income'),
        where('paymentDate', '>=', Timestamp.fromDate(startOfMonth)),
        where('paymentDate', '<=', Timestamp.fromDate(endOfMonth))
      );

      const querySnapshot = await getDocs(q);
      const studentsItem = [];
      let total = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        studentsItem.push({ id: doc.id, ...data });
        total += parseFloat(data.incomeAmount);
      });
      setTotalIncome(total);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const q = query(
        collection(db, 'Expenses'),
        where('expenseDate', '>=', Timestamp.fromDate(startOfMonth)),
        where('expenseDate', '<=', Timestamp.fromDate(endOfMonth))
      );

      const querySnapshot = await getDocs(q);
      const studentsItem = [];
      let total = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        studentsItem.push({ id: doc.id, ...data });
        total += parseFloat(data.expenseAmount);
      });
      setTotalExpenses(total);
    };
    fetchData();
  }, []);



  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAdminRole('admin');
        setLoggedInUser('');
        return;
      }

      try {
        const adminRecord = await resolveAdminRecord(user);
        setAdminRole(normalizeAdminType(adminRecord?.adminType));
        setLoggedInUser(adminRecord?.userName || user.displayName || user.email || '');
      } catch (error) {
        console.error('Failed to resolve dashboard admin role:', error);
        setAdminRole('admin');
        setLoggedInUser(user.displayName || user.email || '');
      }
    });

    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    if (adminRole === 'admin') {
      setShowFinancialData(false);
      return;
    }

    setShowFinancialData(true);
  }, [adminRole]);

  const monthlyEntries = Object.entries(monthlyData);
  const totalYearIncome = monthlyEntries.reduce((sum, [, value]) => sum + (parseFloat(value) || 0), 0);
  const averageMonthlyIncome = totalYearIncome / 12;
  const bestMonthEntry = monthlyEntries.reduce(
    (best, current) => ((parseFloat(current[1]) || 0) > (parseFloat(best[1]) || 0) ? current : best),
    ['Jan', 0]
  );
  const bestMonth = bestMonthEntry[0];
  const bestMonthValue = parseFloat(bestMonthEntry[1]) || 0;
  const shouldBlurFinancialData = adminRole === 'admin' && !showFinancialData;

  return (
    <div className='dashComp'>
      {/** Chart helpers */}
      {(() => {
        return null;
      })()}
      <div className='dashFlex1'>
        <div className='dash1'>
          <div className='dash1Cont'>
            <h4>Welcome</h4>
            <p>{loggedInUser}</p>
          </div>
          <img src={dashOne} alt="" />
        </div>

        <div className='dash2'>
          <div className='dChild1 dashStatCard bg-warning'>
            <div className='dashStatRow'>
              <span className='dashStatIcon'>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </span>
              <div className='dashStatMeta'>
                <h5>{activeStudents}</h5>
                <p>Active Students</p>
              </div>
            </div>
          </div>

          <div className={`dChild2 dashStatCard bg-primary ${shouldBlurFinancialData ? 'dashStatCard--masked' : ''}`}>
            <div className='dashStatRow'>
              <span className='dashStatIcon'>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </span>
              <div className='dashStatMeta'>
                <h5>KES {parseFloat(totalIncome).toLocaleString()}</h5>
                <p>Income This Month</p>
              </div>
            </div>
          </div>

          <div className='dChild3 dashStatCard bg-success'>
            <div className='dashStatRow'>
              <span className='dashStatIcon'>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </span>
              <div className='dashStatMeta'>
                <h5>{totalApplicants}</h5>
                <p>Total Applications</p>
              </div>
            </div>
          </div>

          <div className={`dChild4 dashStatCard bg-danger ${shouldBlurFinancialData ? 'dashStatCard--masked' : ''}`}>
            <div className='dashStatRow'>
              <span className='dashStatIcon'>
                <FontAwesomeIcon icon={faCalendarAlt} />
              </span>
              <div className='dashStatMeta'>
                <h5>KES {parseFloat(totalExpenses).toLocaleString()}</h5>
                <p>Expenses This Month</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <br />
      <br />

      {adminRole === 'admin' && (
        <div className="dashFinancePrivacyBar">
          <div className="dashFinancePrivacyText">
            <strong>{showFinancialData ? 'Financial data visible' : 'Financial data hidden'}</strong>
            <span>Revenue and expense figures stay blurred by default for admin accounts.</span>
          </div>
          <button
            type="button"
            className={`dashFinancePrivacyButton ${showFinancialData ? 'is-active' : ''}`}
            onClick={() => setShowFinancialData((current) => !current)}
          >
            {showFinancialData ? 'Hide figures' : 'Reveal figures'}
          </button>
        </div>
      )}

      <div className={`feeIncomeCard ${shouldBlurFinancialData ? 'feeIncomeCard--masked' : ''}`}>
        <div className="feeIncomeHeader">
          <div>
            <h5>Fee Payment Income</h5>
            <p>Monthly trend for {selectedYear} across all recorded payments.</p>
          </div>
          <div className="feeIncomeYears">
            {years.map((year) => (
              <button
                key={year}
                className={`feeYearBtn ${selectedYear === year ? 'active' : ''}`}
                onClick={() => handleClick(year)}
                aria-pressed={selectedYear === year}
                type="button"
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        <div className="feeIncomeStats">
          <div className="feeIncomeStat">
            <span>Total ({selectedYear})</span>
            <strong>KES {totalYearIncome.toLocaleString()}</strong>
          </div>
          <div className="feeIncomeStat">
            <span>Average / Month</span>
            <strong>KES {averageMonthlyIncome.toLocaleString()}</strong>
          </div>
          <div className="feeIncomeStat">
            <span>Best Month</span>
            <strong>{bestMonth} · KES {bestMonthValue.toLocaleString()}</strong>
          </div>
        </div>

        <div className="feeIncomeChart">
          <Line
            className="theFeeLineGraph"
            datasetIdKey="id"
            data={{
              labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              datasets: [
                {
                  id: 1,
                  label: `Fee payment income (${selectedYear})`,
                  data: [
                    monthlyData.Jan, monthlyData.Feb, monthlyData.Mar, monthlyData.Apr,
                    monthlyData.May, monthlyData.Jun, monthlyData.Jul, monthlyData.Aug,
                    monthlyData.Sep, monthlyData.Oct, monthlyData.Nov, monthlyData.Dec
                  ],
                  backgroundColor: (context) => {
                    const { chart } = context;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return 'rgba(14, 165, 233, 0.15)';
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(14, 165, 233, 0.35)');
                    gradient.addColorStop(1, 'rgba(14, 165, 233, 0.02)');
                    return gradient;
                  },
                  borderColor: '#0ea5e9',
                  pointBackgroundColor: '#0ea5e9',
                  pointBorderColor: '#ffffff',
                  pointBorderWidth: 2,
                  pointRadius: 3,
                  pointHoverRadius: 5,
                  borderWidth: 2.5,
                  fill: true,
                  tension: 0.35
                }
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      let label = context.dataset.label || '';
                      if (label) {
                        label += ': ';
                      }
                      if (context.parsed.y !== null) {
                        label += context.parsed.y.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'KES',
                        });
                      }
                      return label;
                    },
                  },
                },
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: { color: '#94a3b8' }
                },
                y: {
                  grid: { color: 'rgba(15, 27, 45, 0.08)' },
                  ticks: {
                    color: '#94a3b8',
                    callback: (value) => `KES ${Number(value).toLocaleString()}`
                  }
                }
              }
            }}
          />
        </div>
      </div>

      {adminRole === 'superAdmin' && (
      <div className="dashAnalytics">
        <h5>Analytics Snapshot</h5>
        <div className="dashAnalyticsGrid">
          <div className="dashAnalyticsCard">
            <h6>Admissions Funnel</h6>
            <div className="dashChartWrap">
              <Doughnut
                data={{
                  labels: ['Applications', 'Active', 'Graduated', 'Deferred', 'Dropped', 'Expelled', 'Did Not Join'],
                  datasets: [{
                    data: [
                      analytics.funnel.applications,
                      analytics.funnel.active,
                      analytics.funnel.graduated,
                      analytics.funnel.deferred,
                      analytics.funnel.dropped,
                      analytics.funnel.expelled,
                      analytics.funnel.didNotJoin
                    ],
                    backgroundColor: [
                      '#4f46e5',
                      '#0ea5e9',
                      '#22c55e',
                      '#f59e0b',
                      '#ef4444',
                      '#b91c1c',
                      '#6b7280'
                    ],
                    borderWidth: 0
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              <div><span>Applications</span><strong>{analytics.funnel.applications}</strong></div>
              <div><span>Active</span><strong>{analytics.funnel.active}</strong></div>
              <div><span>Graduated</span><strong>{analytics.funnel.graduated}</strong></div>
              <div><span>Deferred</span><strong>{analytics.funnel.deferred}</strong></div>
              <div><span>Dropped</span><strong>{analytics.funnel.dropped}</strong></div>
              <div><span>Expelled</span><strong>{analytics.funnel.expelled}</strong></div>
              <div><span>Did Not Join</span><strong>{analytics.funnel.didNotJoin}</strong></div>
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Weekly/Monthly Intake Trend</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: ['Last 7 days', 'Prev 7 days', 'Last 30 days', 'Prev 30 days'],
                  datasets: [{
                    label: 'Intake',
                    data: [
                      analytics.intake.last7,
                      analytics.intake.prev7,
                      analytics.intake.last30,
                      analytics.intake.prev30
                    ],
                    backgroundColor: ['#0ea5e9', '#94a3b8', '#22c55e', '#86efac'],
                    borderRadius: 8,
                    barThickness: 22
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { grid: { color: 'rgba(15, 27, 45, 0.06)' }, beginAtZero: true }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              <div><span>Last 7 days</span><strong>{analytics.intake.last7}</strong></div>
              <div><span>Previous 7 days</span><strong>{analytics.intake.prev7}</strong></div>
              <div><span>Last 30 days</span><strong>{analytics.intake.last30}</strong></div>
              <div><span>Previous 30 days</span><strong>{analytics.intake.prev30}</strong></div>
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Top Performing Sources</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: analytics.topSources.length ? analytics.topSources.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Leads',
                    data: analytics.topSources.length ? analytics.topSources.map((item) => item.count) : [0],
                    backgroundColor: '#6366f1',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.topSources.length === 0 && <p className="dashAnalyticsEmpty">No source data yet.</p>}
              {analytics.topSources.map((item) => (
                <div key={item.name}><span>{item.name}</span><strong>{item.count}</strong></div>
              ))}
            </div>
            <div className="dashAnalyticsDivider"></div>
            <h6 className="dashAnalyticsSubTitle">Latest Top Sources (30 days)</h6>
            <div className="dashChartWrap dashChartSmall">
              <Bar
                data={{
                  labels: analytics.topSourcesLatest.length ? analytics.topSourcesLatest.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Leads',
                    data: analytics.topSourcesLatest.length ? analytics.topSourcesLatest.map((item) => item.count) : [0],
                    backgroundColor: '#22c55e',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.topSourcesLatest.length === 0 && <p className="dashAnalyticsEmpty">No recent source data yet.</p>}
              {analytics.topSourcesLatest.map((item) => (
                <div key={item.name}><span>{item.name}</span><strong>{item.count}</strong></div>
              ))}
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Completion Rate by Course</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: analytics.completionByCourse.length ? analytics.completionByCourse.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Completion %',
                    data: analytics.completionByCourse.length ? analytics.completionByCourse.map((item) => item.rate) : [0],
                    backgroundColor: '#14b8a6',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, max: 100, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.completionByCourse.length === 0 && <p className="dashAnalyticsEmpty">No completion data yet.</p>}
              {analytics.completionByCourse.map((item) => (
                <div key={item.name}><span>{item.name}</span><strong>{item.rate}%</strong></div>
              ))}
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Drop-off Reasons</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: analytics.dropReasons.length ? analytics.dropReasons.map((item) => item.reason) : ['No data'],
                  datasets: [{
                    label: 'Count',
                    data: analytics.dropReasons.length ? analytics.dropReasons.map((item) => item.count) : [0],
                    backgroundColor: '#ef4444',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  indexAxis: 'y',
                  scales: {
                    x: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } },
                    y: { grid: { display: false } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.dropReasons.length === 0 && <p className="dashAnalyticsEmpty">No drop-off reasons recorded.</p>}
              {analytics.dropReasons.map((item) => (
                <div key={item.reason}><span>{item.reason}</span><strong>{item.count}</strong></div>
              ))}
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Payment Health</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: ['Income', 'Expenses', 'Net'],
                  datasets: [{
                    label: 'KES',
                    data: [
                      parseFloat(totalIncome),
                      parseFloat(totalExpenses),
                      parseFloat(totalIncome - totalExpenses)
                    ],
                    backgroundColor: ['#22c55e', '#ef4444', '#0ea5e9'],
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              <div><span>Income (MTD)</span><strong>KES {parseFloat(totalIncome).toLocaleString()}</strong></div>
              <div><span>Expenses (MTD)</span><strong>KES {parseFloat(totalExpenses).toLocaleString()}</strong></div>
              <div><span>Net</span><strong>KES {parseFloat(totalIncome - totalExpenses).toLocaleString()}</strong></div>
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Debt Risk</h6>
            <div className="dashChartWrap">
              <Doughnut
                data={{
                  labels: ['Due in 30 days', 'Other'],
                  datasets: [{
                    data: [
                      analytics.debtRisk.dueSoon,
                      Math.max(analytics.debtRisk.totalCount - analytics.debtRisk.dueSoon, 0)
                    ],
                    backgroundColor: ['#f59e0b', '#94a3b8'],
                    borderWidth: 0
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'bottom' } }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              <div><span>Active loans</span><strong>{analytics.debtRisk.totalCount}</strong></div>
              <div><span>Due in 30 days</span><strong>{analytics.debtRisk.dueSoon}</strong></div>
              <div><span>Total principal</span><strong>KES {parseFloat(analytics.debtRisk.totalPrincipal).toLocaleString()}</strong></div>
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Cohort Progress</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: analytics.cohortProgress.length ? analytics.cohortProgress.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Completion %',
                    data: analytics.cohortProgress.length
                      ? analytics.cohortProgress.map((item) => (item.total ? Math.round((item.completed / item.total) * 100) : 0))
                      : [0],
                    backgroundColor: '#6366f1',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, max: 100, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.cohortProgress.length === 0 && <p className="dashAnalyticsEmpty">No cohort data yet.</p>}
              {analytics.cohortProgress.map((item) => (
                <div key={item.name}>
                  <span>{item.name}</span>
                  <strong>{item.completed}/{item.total} completed</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="dashAnalyticsCard">
            <h6>Instructor Load</h6>
            <div className="dashChartWrap">
              <Bar
                data={{
                  labels: analytics.instructorLoad.length ? analytics.instructorLoad.map((item) => item.name) : ['No data'],
                  datasets: [{
                    label: 'Classes',
                    data: analytics.instructorLoad.length ? analytics.instructorLoad.map((item) => item.count) : [0],
                    backgroundColor: '#0ea5e9',
                    borderRadius: 8
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, grid: { color: 'rgba(15, 27, 45, 0.06)' } }
                  }
                }}
              />
            </div>
            <div className="dashAnalyticsList">
              {analytics.instructorLoad.length === 0 && <p className="dashAnalyticsEmpty">No instructor load yet.</p>}
              {analytics.instructorLoad.map((item) => (
                <div key={item.name}><span>{item.name}</span><strong>{item.count} classes</strong></div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default DashComp;
